// src/components/student/CourseEnrollButton.tsx
'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { 
  Lock, CheckCircle2, ArrowRight, Loader2, 
  IndianRupee, Sparkles, AlertCircle 
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'

declare global {
  interface Window {
    Razorpay: any
  }
}

interface CourseEnrollButtonProps {
  courseId: string
  courseSlug: string
  courseTitle: string
  monthlyPrice: number
  fullPrice: number | null
  discount: number
  isEnrolled: boolean
  daysRemaining?: number | null
}

const VERIFY_TIMEOUT_MS = 20_000

export default function CourseEnrollButton({
  courseId,
  courseSlug,
  courseTitle,
  monthlyPrice,
  fullPrice,
  discount,
  isEnrolled,
  daysRemaining,
}: CourseEnrollButtonProps) {
  const router = useRouter()
  const { data: session, status: authStatus } = useSession()

  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'full'>('monthly')
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(false)

  // If already enrolled, show direct Classroom button
  if (isEnrolled) {
    return (
      <div className="space-y-3">
        <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <div className="text-xs">
            <p className="font-bold">You are Enrolled in this Batch</p>
            <p className="text-emerald-700">
              {daysRemaining !== null && daysRemaining !== undefined
                ? `${daysRemaining} days remaining in current month`
                : 'Access active'}
            </p>
          </div>
        </div>

        <Button
          onClick={() => router.push(`/courses/${courseSlug}/classroom`)}
          className="w-full py-4 text-base font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl shadow-lg shadow-emerald-200 gap-2 transition-all"
        >
          <span>Enter Live Classroom</span>
          <ArrowRight className="w-5 h-5" />
        </Button>
      </div>
    )
  }

  const basePrice = selectedPlan === 'full' && fullPrice ? fullPrice : monthlyPrice
  const discountAmount = Math.round(basePrice * (discount / 100))
  const finalPrice = Math.max(1, basePrice - discountAmount)

  const handleEnroll = async () => {
    // 1. If not logged in, redirect to login with return URL
    if (authStatus !== 'authenticated') {
      toast.info('Please sign in or enter phone to enroll')
      router.push(`/login?callbackUrl=${encodeURIComponent(`/courses/${courseSlug}`)}`)
      return
    }

    try {
      setLoading(true)

      const orderResponse = await fetch('/api/payments/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'course',
          courseId,
          pricingPlan: selectedPlan,
        }),
      })

      const orderData = await orderResponse.json()

      if (!orderResponse.ok) {
        throw new Error(orderData.error || 'Failed to initialize payment')
      }

      if (orderData.alreadyOwned) {
        toast.success('You are already enrolled!')
        router.push(`/courses/${courseSlug}/classroom`)
        return
      }

      if (!window.Razorpay) {
        throw new Error('Payment gateway not loaded. Please refresh the page.')
      }

      const options = {
        key: orderData.key,
        amount: orderData.amount * 100,
        currency: 'INR',
        name: 'Mockzy',
        description: `${courseTitle} (${selectedPlan === 'full' ? 'Full Batch' : 'Monthly Pass'})`,
        order_id: orderData.orderId,
        prefill: {
          name: session.user.name || '',
          email: session.user.email || '',
          contact: (session.user as any)?.phone || '',
        },
        theme: { color: '#7c3aed' },

        handler: async function (response: any) {
          setVerifying(true)
          setLoading(false)

          try {
            const verifyRes = await fetch('/api/payments/verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
                purchaseId: orderData.purchaseId,
              }),
            })

            const verifyData = await verifyRes.json()

            if (verifyRes.ok && verifyData.success) {
              toast.success('🎉 Enrollment Successful!', {
                description: 'Welcome to the batch! Taking you to your live classroom.',
              })
              router.push(`/courses/${courseSlug}/classroom`)
            } else {
              toast.error(verifyData.error || 'Payment verification failed. Please contact support.')
            }
          } catch {
            toast.error('Network error during verification')
          } finally {
            setVerifying(false)
          }
        },

        modal: {
          ondismiss: function () {
            setLoading(false)
            setVerifying(false)
          },
        },
      }

      const razorpayInstance = new window.Razorpay(options)
      razorpayInstance.open()
    } catch (err: any) {
      toast.error(err.message || 'Payment initiation failed')
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Plan Selector */}
      <div className="grid grid-cols-2 gap-2 p-1.5 bg-gray-100 rounded-2xl">
        <button
          type="button"
          onClick={() => setSelectedPlan('monthly')}
          className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
            selectedPlan === 'monthly'
              ? 'bg-white text-purple-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Monthly Pass (30 Days)
        </button>

        {fullPrice ? (
          <button
            type="button"
            onClick={() => setSelectedPlan('full')}
            className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
              selectedPlan === 'full'
                ? 'bg-white text-purple-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Full Batch (Year Pass)
          </button>
        ) : (
          <div className="py-2 px-3 text-center text-xs text-gray-400 font-medium">
            Pay as you go
          </div>
        )}
      </div>

      {/* Price Display */}
      <div className="p-4 rounded-2xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-purple-600">
              {selectedPlan === 'full' ? 'Full Batch Fee' : 'Monthly Batch Fee'}
            </p>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="text-2xl font-extrabold text-gray-900">
                ₹{finalPrice.toLocaleString('en-IN')}
              </span>
              {discount > 0 && (
                <span className="text-sm text-gray-400 line-through">
                  ₹{basePrice.toLocaleString('en-IN')}
                </span>
              )}
              <span className="text-xs text-gray-500 font-normal">
                {selectedPlan === 'full' ? 'one-time' : '/ month'}
              </span>
            </div>
          </div>

          {discount > 0 && (
            <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold">
              {discount}% OFF
            </span>
          )}
        </div>
      </div>

      {/* Enroll Button */}
      <Button
        onClick={handleEnroll}
        disabled={loading || verifying}
        className="w-full py-4 text-base font-bold bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-2xl shadow-xl shadow-purple-200 gap-2 transition-all hover:shadow-purple-300"
      >
        {loading || verifying ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{verifying ? 'Verifying Payment...' : 'Opening Razorpay...'}</span>
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5 text-amber-300" />
            <span>Enroll Now • ₹{finalPrice.toLocaleString('en-IN')}</span>
          </>
        )}
      </Button>

      <p className="text-center text-[11px] text-gray-400 flex items-center justify-center gap-1.5">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
        Safe & encrypted checkout powered by Razorpay
      </p>
    </div>
  )
}
